import React, { type ReactNode } from 'react';
import { Text } from '@atlaskit/primitives';
import type { Flag } from '@atlassian/jira-flags';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';
import type { CreatedIssue } from './types.tsx';

export const getCreatedIssueFieldValue = (
	createdIssue: CreatedIssue,
	fieldId: string,
): string | null => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find((field) => field?.fieldId === fieldId);

	switch (issueField?.__typename) {
		case 'JiraStatusField':
			return issueField.status?.statusId ?? null;
		case 'JiraPriorityField':
			return issueField.priority?.priorityId ?? null;
		case 'JiraSingleSelectUserPickerField':
			return issueField.user?.accountId ?? null;
		case 'JiraSingleSelectField':
			return issueField.fieldOption?.optionId ?? null;
		case 'JiraSprintField':
			return issueField.selectedSprintsConnection?.edges?.[0]?.node?.sprintId ?? null;
		case 'JiraNumberField':
			return issueField.number?.toString() ?? null;
		default:
			return null;
	}
};

export const getStatusName = (createdIssue: CreatedIssue) => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find(
		(field) => field?.fieldId === STATUS_TYPE,
	);

	return issueField?.status?.name;
};

const Bold = (chunk: ReactNode) => <Text as="strong">{chunk}</Text>;
export const getIssueCreateSuccessFlag = ({
	issueKey,
	statusName,
}: {
	issueKey: string;
	statusName?: string | null;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: statusName
		? [messages.workItemChangedStatusDescription, { issueKey, statusName, Bold }]
		: undefined,
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});

export const getIssueCreateRerankingPromptSuccessFlag = ({
	issueKey,
}: {
	issueKey: string;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [messages.rerankingPromptDescription, { issueKey, Bold }],
	actions: [
		// TODO: JSC-1895 Add 'sort by rank' action to reranking prompt
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});

export const getIssueCreateHiddenFlag = ({ issueKey }: { issueKey: string }): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [
		fg('jira-issue-terminology-refresh-m3')
			? messages.workItemHiddenDescription
			: messages.issueHiddenDescription,
		{ issueKey, Bold },
	],
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});
